import { authConfig } from "./authConfig";
// Thrown from plain functions, not components — hence the i18next singleton
// rather than the useTranslation hook.
import { i18n } from "../../i18n";

/* global Office */

interface DialogSuccess {
  status: "success";
  token: string;
  username?: string;
}
interface DialogFailure {
  status: "error";
  error: string;
}
type DialogMessage = DialogSuccess | DialogFailure;

/**
 * Opens an Office dialog running MSAL.js interactive sign-in (dialog.html) and
 * resolves with a Microsoft Graph access token. Used as the fallback for
 * personal Microsoft accounts where Office SSO is unsupported. REQ-AUTH-002.
 */
export function signInWithDialog(): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    Office.context.ui.displayDialogAsync(
      authConfig.dialogUrl,
      { height: 60, width: 30 },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(result.error?.message ?? i18n.t("errors.dialogOpenFailed")));
          return;
        }

        const dialog = result.value;

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          let message: DialogMessage;
          try {
            message = JSON.parse((arg as { message: string }).message) as DialogMessage;
          } catch {
            dialog.close();
            reject(new Error(i18n.t("errors.dialogUnreadable")));
            return;
          }
          dialog.close();
          if (message.status === "success") {
            resolve(message.token);
          } else {
            reject(new Error(message.error));
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          // Fired when the user closes the dialog or navigation fails.
          reject(new Error(i18n.t("errors.dialogCancelled")));
        });
      }
    );
  });
}
